import { useEffect, useState, useRef } from "react";
import { Upload, FileText, Download, Eye, RefreshCw, Trash2, File, Image, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { EmptyState } from "@/components/EmptyState";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fileSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function DocumentsPanel({ entityType, entityId, compact = false }) {
  const { meta } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState(entityType === "lead" ? "Quotation" : "Other");
  const [confirm, setConfirm] = useState(null);
  const fileRef = useRef();
  const replaceRef = useRef();
  const [replaceId, setReplaceId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/documents?entity_type=${entityType}&entity_id=${entityId}`); setDocs(data); }
    catch (e) { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [entityType, entityId]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("entity_type", entityType); fd.append("entity_id", entityId); fd.append("doc_type", docType);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": undefined } });
      toast.success("Document uploaded");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doReplace = async (file) => {
    if (!file || !replaceId) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.put(`/documents/${replaceId}`, fd, { headers: { "Content-Type": undefined } }); toast.success("Document replaced"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setReplaceId(null); if (replaceRef.current) replaceRef.current.value = ""; }
  };

  const del = async () => { await api.delete(`/documents/${confirm.id}`); toast.success("Document deleted"); setConfirm(null); load(); };
  const url = (d, dl) => `${BACKEND}/api/documents/${d.id}/download${dl ? "?download=true" : ""}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-4">
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger data-testid="doc-type-select" className="h-10 w-44 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>{(meta?.doc_types || ["Quotation", "Other"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <input ref={fileRef} type="file" className="hidden" data-testid="doc-file-input" onChange={(e) => upload(e.target.files[0])} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv" />
        <input ref={replaceRef} type="file" className="hidden" onChange={(e) => doReplace(e.target.files[0])} />
        <Button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10 gap-1.5 font-semibold">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Document
        </Button>
        <span className="text-xs text-slate-400">PDF, images or office files · up to 25 MB</span>
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-lg bg-slate-100" />)}</div>
      ) : docs.length === 0 ? (
        <EmptyState testid="documents-empty" icon={FileText} title="No documents yet" description="Upload the original quotation PDF or supporting documents." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          {docs.map((d) => {
            const isImg = (d.content_type || "").startsWith("image/");
            const Icon = isImg ? Image : (d.content_type === "application/pdf" ? FileText : File);
            return (
              <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 border-b border-slate-50 px-4 py-3 last:border-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: "#0057A814", color: "#0057A8" }}><Icon className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-800">{d.original_filename}</div>
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-400">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-500">{d.doc_type}</span>
                    <span>{fileSize(d.size)}</span><span>· {d.uploaded_by_name}</span><span>· {formatDate(d.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <a href={url(d)} target="_blank" rel="noreferrer" title="View" data-testid={`doc-view-${d.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Eye className="h-4 w-4" /></a>
                  <a href={url(d, true)} title="Download" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Download className="h-4 w-4" /></a>
                  <button title="Replace" onClick={() => { setReplaceId(d.id); replaceRef.current?.click(); }} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><RefreshCw className="h-4 w-4" /></button>
                  <button title="Delete" onClick={() => setConfirm(d)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} title="Delete document?" description="This will remove the document. This cannot be undone." confirmText="Delete" onConfirm={del} />
    </div>
  );
}
